// The shared context: ring buffers, redaction, the "is this ours?" checks, and
// loading of the other two bundles. Every hook and both lazy bundles use it.

import { RingBuffer } from './ring-buffer.js';
import { GROUPS, createEntry, groupFor, sortByTime } from '../shared/timeline.js';
import { createRedactor } from './redact.js';
import { createUrlMatcher } from './config.js';

/**
 * @param {ReturnType<import('./config.js').readConfig>} config
 * @param {{ assetBase: string, session: ReturnType<import('./session.js').startSession> }} options
 */
export function createContext(config, { assetBase, session }) {
  /** @type {Record<string, RingBuffer>} */
  const buffers = {};
  for (const group of GROUPS) {
    buffers[group] = new RingBuffer({
      maxEntries: config.maxEntries,
      maxAgeMs: config.logWindow * 1000,
    });
  }
  const listeners = new Set();
  const modules = {};
  const loading = {};

  const ctx = {
    config,
    assetBase,
    buffers,
    modules,
    session,
    sessionId: session.id,
    pageId: session.page.id,
    redact: createRedactor(config),
    isOwnUrl: createUrlMatcher([config.endpoint, assetBase], config.ignoreUrls),
    /** @type {{ element: HTMLElement, root: ShadowRoot } | null} */
    host: null,
    /** @type {null | (() => unknown)} */
    metadataFn: null,
    /** Original browser functions, before our hooks wrapped them. */
    originals: {},
    /** Re-entrancy guard so our own work is never captured. */
    busy: false,
    /**
     * CSP violations before this time are ours (screenshots copy styles and re-fetch images),
     * so the CSP hook skips them. The browser reports violations a moment after they happen.
     */
    cspMutedUntil: 0,

    /**
     * Records a timeline entry.
     * @param {string} kind
     * @param {import('../shared/timeline.js').Level} level
     * @param {string} title
     * @param {Record<string, unknown>} [detail]
     * @param {number} [t]
     */
    push(kind, level, title, detail = {}, t = Date.now()) {
      const entry = createEntry(kind, level, title, detail, t);
      entry.page = ctx.pageId;
      buffers[groupFor(kind)].push(entry);
      for (const fn of listeners) {
        try {
          fn(entry);
        } catch {
          // a broken listener must never break capture
        }
      }
      return entry;
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    /**
     * Runs the site's sanitiser for this type, if there is one. Returns null to drop.
     * A sanitiser that throws gets the entry replaced, never sent raw.
     */
    sanitise(type, detail) {
      const fn = config.sanitisers && config.sanitisers[type];
      if (typeof fn !== 'function') return detail;
      try {
        const out = fn(detail);
        if (out === null || out === false) return null;
        return out && typeof out === 'object' ? out : detail;
      } catch (error) {
        return { sanitiserFailed: true, error: String(error && error.message) };
      }
    },

    /** True when the event happened inside our own UI. */
    isOwnEvent(event) {
      const element = ctx.host && ctx.host.element;
      if (!element) return false;
      try {
        return event.composedPath().includes(element);
      } catch {
        return false;
      }
    },

    isOwnNode(node) {
      const element = ctx.host && ctx.host.element;
      return !!(element && node && (node === element || element.contains(node)));
    },

    /** All entries, oldest first. */
    entries(now = Date.now()) {
      const all = [];
      for (const group of GROUPS) all.push(...buffers[group].toArray(now));
      return sortByTime(all);
    },

    /**
     * Loads replay.js or recorder.js from the loader's own version folder.
     * @param {'replay' | 'recorder'} name
     */
    load(name) {
      if (modules[name]) return Promise.resolve(modules[name]);
      if (loading[name]) return loading[name];
      const inject = (crossOrigin) =>
        new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `${assetBase}${name}.js`;
          script.async = true;
          if (crossOrigin) script.crossOrigin = 'anonymous';
          script.setAttribute('data-adido-bugs-module', name);
          script.onload = () => {
            if (modules[name]) resolve(modules[name]);
            else reject(new Error(`${name}.js loaded but did not register`));
          };
          script.onerror = () => {
            script.remove();
            reject(new Error(`Could not load ${script.src}`));
          };
          (document.head || document.documentElement).appendChild(script);
        });
      // crossorigin="anonymous" gives full error details for our own code, but needs
      // Access-Control-Allow-Origin on the asset host. Retry without it if that's missing.
      loading[name] = inject(true)
        .catch(() => inject(false))
        .catch((error) => {
          delete loading[name];
          throw error;
        });
      return loading[name];
    },

    /** Opens the report panel, loading what it needs first. */
    async open() {
      if (!modules.replay) {
        // replay: false on this site, or still loading. Start it now so the report
        // at least has a replay from this point on.
        ctx.load('replay').catch(() => {});
      }
      const recorder = await ctx.load('recorder');
      recorder.open(ctx);
    },
  };

  return ctx;
}
